import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  Robot,
  ChatCircle,
  TrendUp,
  ArrowRight,
  ArrowsClockwise,
  Stack,
  Question,
} from '@phosphor-icons/react';

// Simple sparkline component
const Sparkline = ({ data, color = '#8b5cf6' }) => {
  if (!data || data.length === 0) return null;
  
  const values = data.map(d => d.dau_ask || 0);
  const max = Math.max(...values, 1);
  const min = Math.min(...values);
  const range = max - min || 1;
  
  const width = 120;
  const height = 32;
  const padding = 2;
  
  const points = values.map((val, i) => {
    const x = padding + (i / (values.length - 1 || 1)) * (width - padding * 2);
    const y = height - padding - ((val - min) / range) * (height - padding * 2);
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {values.length > 0 && (
        <circle
          cx={width - padding}
          cy={height - padding - ((values[values.length - 1] - min) / range) * (height - padding * 2)}
          r="3"
          fill={color}
        />
      )}
    </svg>
  );
};

export const AskStewieAdoptionWidget = ({ department = 'substation' }) => {
  const { api, isSupervisorPlus } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!isSupervisorPlus()) {
        setLoading(false);
        return;
      }
      
      try {
        const [adoptionRes, clustersRes] = await Promise.all([
          api.get(`/adoption/${department}/askstewie/today`),
          api.get(`/adoption/${department}/askstewie/clusters/today/summary?limit=3`)
        ]);
        setData(adoptionRes.data);
        setClusters(clustersRes.data?.top_clusters || []);
      } catch (error) {
        console.error('Failed to fetch AskStewie adoption data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 300000);
    return () => clearInterval(interval);
  }, [api, department, isSupervisorPlus]);

  if (!isSupervisorPlus()) {
    return null;
  }

  const getTrendIndicator = () => {
    if (!data?.sparkline || data.sparkline.length < 2) return null;
    
    const lastTwo = data.sparkline.slice(-2);
    const trend = (lastTwo[1]?.dau_ask || 0) - (lastTwo[0]?.dau_ask || 0);
    
    if (trend > 0) {
      return <Badge className="bg-emerald-100 text-emerald-700 text-xs">+{trend}</Badge>;
    } else if (trend < 0) {
      return <Badge className="bg-red-100 text-red-700 text-xs">{trend}</Badge>;
    }
    return null;
  };

  return (
    <Card 
      className="border-2 border-violet-100 bg-gradient-to-br from-violet-50 to-purple-50 hover:shadow-lg transition-all cursor-pointer group"
      onClick={() => navigate(`/analytics/adoption/${department}/askstewie`)}
      data-testid="askstewie-adoption-widget"
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Robot size={22} className="text-white" weight="fill" />
            </div>
            <div>
              <CardTitle className="text-lg">AskStewie Adoption</CardTitle>
              <CardDescription className="text-xs">AI assistant engagement</CardDescription>
            </div>
          </div>
          <ArrowRight size={20} className="text-slate-400 group-hover:text-violet-600 transition-colors" />
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <ArrowsClockwise size={24} className="animate-spin text-violet-400" />
          </div>
        ) : data ? (
          <div className="space-y-4">
            {/* Metrics Grid */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white rounded-lg p-3 border border-violet-100">
                <div className="flex items-center gap-1 mb-1">
                  <ChatCircle size={14} className="text-violet-500" />
                  <span className="text-xs text-slate-500">DAU</span>
                </div>
                <div className="flex items-center gap-2">
                  <p className="text-xl font-bold text-slate-900">{data.dau_ask || 0}</p>
                  {getTrendIndicator()}
                </div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-emerald-100">
                <div className="flex items-center gap-1 mb-1">
                  <Question size={14} className="text-emerald-500" />
                  <span className="text-xs text-slate-500">Questions</span>
                </div>
                <p className="text-xl font-bold text-slate-900">{data.questions_count || 0}</p>
              </div>
              <div className="bg-white rounded-lg p-3 border border-purple-100">
                <div className="flex items-center gap-1 mb-1">
                  <TrendUp size={14} className="text-purple-500" />
                  <span className="text-xs text-slate-500">Total Users</span>
                </div>
                <p className="text-xl font-bold text-slate-900">{data.total_ask_users_to_date || 0}</p>
              </div>
            </div>
            
            {/* Sparkline */}
            {data.sparkline && data.sparkline.length > 0 && (
              <div className="flex items-center justify-between bg-white rounded-lg p-3 border">
                <span className="text-xs text-slate-500">7-Day Trend</span>
                <Sparkline data={data.sparkline} color="#8b5cf6" />
              </div>
            )}
            
            {/* Top Clusters (Most Common Questions) */}
            {clusters.length > 0 && (
              <div className="bg-white rounded-lg p-3 border">
                <div className="flex items-center gap-1 mb-2">
                  <Stack size={14} className="text-violet-500" />
                  <span className="text-xs text-slate-500">Most Common Questions Today</span>
                </div>
                <div className="space-y-2">
                  {clusters.slice(0, 2).map((c, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-xs text-slate-700 truncate flex-1 mr-2">
                        {c.canonical?.substring(0, 40)}{c.canonical?.length > 40 ? '...' : ''}
                      </span>
                      <Badge variant="outline" className="text-xs bg-violet-50 text-violet-700">
                        {c.count}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-6 text-slate-500">
            <Robot size={32} className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm">No AskStewie data available</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AskStewieAdoptionWidget;
